import { formatPln } from '../catalog/money';
import { marginLabel, monthLabel, turnoverTotal } from './format';
import type { Cash, Totals, YearSummary } from './types';

const money = (v: number | undefined) => (v === undefined ? '—' : formatPln(v));

/**
 * The year as 12 months (F29) with the F45 columns the reader may see: revenue, events' and fixed costs, estimated
 * income, margin, turnover, payments, missing (its total: the state at the year's end or today), forecast of inflows,
 * investments, purchases to the catalog. A month's button opens it.
 */
export function YearTable({ data, onMonth }: { data: YearSummary; onMonth: (month: string) => void }) {
  const t = data.totals;
  const cols: Array<{ key: string; title: string; cell: (r: Totals & Cash) => React.ReactNode }> = [
    { key: 'revenue', title: 'Przychód', cell: (r) => formatPln(r.revenueConfirmedGrosze + r.revenueForecastGrosze) },
    { key: 'events', title: 'Koszty wydarzeń', cell: (r) => formatPln(r.eventCostsGrosze) },
    // owner 2026-11-04: own transport of each month — actual fuel when the month has any, else the estimates
    ...(t.transport
      ? [{ key: 'transport', title: 'Transport / paliwo', cell: (r: Totals) => (r.transport ? <span title={r.transport.basis === 'rzeczywiste' ? 'paliwo rzeczywiste' : 'szacunek z km wydarzeń'}>{formatPln(r.transport.transportGrosze)}{r.transport.basis === 'rzeczywiste' ? ' (paliwo)' : ''}</span> : '—') }]
      : []),
    { key: 'fixed', title: 'Koszty stałe', cell: (r) => formatPln(r.fixedCostsGrosze) },
    // owner 2026-10-22 01:34: done company tasks' costs, only for whoever sees the tasks' "Koszt"
    ...(t.taskCostsGrosze !== undefined ? [{ key: 'tasks', title: 'Koszty zadań', cell: (r: Totals) => money(r.taskCostsGrosze) }] : []),
    { key: 'result', title: 'Szacowany dochód', cell: (r) => <span className={r.resultGrosze < 0 ? 'neg' : undefined}>{formatPln(r.resultGrosze)}</span> },
    { key: 'margin', title: 'Marża', cell: (r) => marginLabel(r.marginBp) },
    ...(t.turnover ? [{ key: 'turnover', title: 'Obroty', cell: (r: Cash) => money(r.turnover ? turnoverTotal(r.turnover) : undefined) }] : []),
    ...(t.payments ? [{ key: 'payments', title: 'Wpłaty', cell: (r: Cash) => money(r.payments?.totalGrosze) }] : []),
    ...(t.missing ? [{ key: 'missing', title: 'Brak wpłat (stan)', cell: (r: Cash) => <span className={r.missing && r.missing.overdueGrosze > 0 ? 'neg' : undefined}>{money(r.missing?.overdueGrosze)}</span> }] : []),
    ...(t.forecast ? [{ key: 'forecast', title: 'Prognoza wpływów', cell: (r: Cash) => money(r.forecast?.totalGrosze) }] : []),
    ...(t.investments ? [{ key: 'investments', title: 'Inwestycje', cell: (r: Cash) => money(r.investments?.netGrosze) }] : []),
    ...(t.catalogPurchases ? [{ key: 'catalog', title: 'Zakupy do katalogu', cell: (r: Cash) => money(r.catalogPurchases?.netGrosze) }] : []),
  ];
  return (
    <div className="table-wrap">
      <table className="table fin-table" aria-label={`Rok ${data.year}`}>
        <thead>
          <tr>
            <th scope="col">Miesiąc</th>
            {cols.map((c) => (
              <th key={c.key} scope="col" className="num">
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.months.map((m) => (
            <tr key={m.month} className="clickable" onClick={() => onMonth(m.month)}>
              <th scope="row">
                <button className="btn ghost small" type="button" onClick={() => onMonth(m.month)}>
                  {monthLabel(m.month).split(' ')[0]}
                </button>
              </th>
              {cols.map((c) => (
                <td key={c.key} className="num">
                  {c.cell(m)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Rok {data.year}</th>
            {cols.map((c) => (
              <td key={c.key} className="num">
                {c.cell(t)}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
